import { Name, createName } from "./../models/Name";
import { stringOrUndefined } from "./utils";
import moment from "moment";

import dicomParser from "dicom-parser";
import cornerstone from "cornerstone-core";
import { convertBytes } from "dicom-character-set";

// read a file as array of Uint8
export const readUint8Array = async (file: File) => {
  // the file reader
  let reader = new FileReader();

  // wrap into a promise
  return new Promise<Uint8Array>((resolve, reject) => {
    // error handler
    reader.onerror = () => {
      reader.abort();
      reject("Failed to read file " + file.name);
    };

    // load handler
    reader.onload = () => {
      resolve(new Uint8Array(reader.result as ArrayBuffer));
    };

    // read file as byte array
    reader.readAsArrayBuffer(file);
  });
};

// try to read and parse as dicom
// returns dataset or undefined
export const readDicomFile = async (file: File) => {
  try {
    let dicom = await readUint8Array(file);
    return dicomParser.parseDicom(dicom); // success
  } catch (e) {
    return undefined; // failed
  }
};

export const stringFromDataset = (
  dataset: any,
  attribute: string
): string | undefined => {
  if (!dataset) return undefined;

  // get attribute (element)
  const element = dataset.elements[attribute];
  if (!element || element.length === 0) return undefined;

  // extract value as byte array
  const value = new Uint8Array(
    dataset.byteArray.buffer,
    element.dataOffset,
    element.length
  );
  if (!value || value.length === 0) return undefined;

  // convert byte array
  const result = stringOrUndefined(
    convertBytes(dataset.string("x00080005"), value, {
      vr: element.vr,
    })
  );

  // remove any padding
  return result?.trimEnd();
};

export const nameFromDicomString = (name: string): Name => {
  // split name string
  const groups = name.split("=", 3);
  if (groups.length >= 1 && groups[0].length === 0) return createName(); // empty name

  // split group (only first group used)
  const comps = groups[0].split("^", 5);
  return createName(comps[0], comps[1], comps[2], comps[3], comps[4]);
};

export const dateFromDicomString = (date: string): string | null => {
  if (date.trim().length === 0) return null;
  const datemoment = moment(date);
  if (!datemoment.isValid()) return null;
  return datemoment.format("YYYYMMDD");
};

export const sexFromDicomString = (sex: string): string => {
  if (sex.toUpperCase() === "M") return "M";
  if (sex.toUpperCase() === "F") return "F";
  return "O";
};

// initalize Cornerstone Wado Image Loader
// NOTE: will use web workers (for decoding)
export const initWado = (loader: any) => {
  try {
    loader.webWorkerManager.initialize({
      maxWebWorkers: 4,
      startWebWorkersOnDemand: true,
      webWorkerTaskPaths: [],
      taskConfiguration: {
        decodeTask: {
          initializeCodecsOnStartup: false,
          usePDFJS: false,
          strict: true,
        },
      },
    });
  } catch (error) {
    throw new Error("cornerstoneWADOImageLoader is not loaded");
  }

  // setup externals
  loader.external.cornerstone = cornerstone;
  loader.external.dicomParser = dicomParser;

  // configure
  loader.configure({
    beforeSend: function (xhr: any) {
      // Add custom headers here (e.g. auth tokens)
      //xhr.setRequestHeader('x-auth-token', 'my auth token');
    },
    useWebWorkers: true,
  });
};
